import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// The session survives a page reload (token in localStorage), so a reload mid-match
// reconnects into the same game. Guests keep their Guest-1234 identity for 24 h.
const STORAGE_KEY = "snooker.auth";

export interface AuthenticationState {
    id: string | null;
    email: string | null;
    username: string | null;
    tokenType: string | null;
    accessToken: string | null;
    expiresIn: number | null;
    refreshToken: string | null;
    guest: boolean;
}

const nullState: AuthenticationState = {
    id: null,
    username: null,
    email: null,
    tokenType: null,
    accessToken: null,
    expiresIn: null,
    refreshToken: null,
    guest: false,
};

function loadState(): AuthenticationState {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
            const saved = JSON.parse(raw);
            if (saved && saved.accessToken && saved.username) return {...nullState, ...saved};
        }
    } catch {}
    return nullState;
}

interface LoginPayload {
    newState: Partial<AuthenticationState>,
    remember?: boolean
}

export interface UpdateTokensPayload {
    tokenType: string,
    accessToken: string,
    expiresIn: number,
    refreshToken: string
}

const authenticationSlice = createSlice({
    name: "Authentication",
    initialState: loadState(),
    reducers: {
        login(state, action: PayloadAction<LoginPayload>) {
            const n = action.payload.newState;
            Object.assign(state, nullState, {
                id: n.id ?? null, email: n.email ?? null, username: n.username ?? null,
                tokenType: n.tokenType ?? "Bearer", accessToken: n.accessToken ?? null,
                expiresIn: n.expiresIn ?? null, guest: !!n.guest,
            });
            try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch {}
        },
        updateTokens(state, action: PayloadAction<UpdateTokensPayload>) {
            Object.assign(state, action.payload)
        },
        logout(state) {
            try { localStorage.removeItem(STORAGE_KEY); } catch {}
            Object.assign(state, nullState);
        }
    }
});

export const { login, logout, updateTokens } = authenticationSlice.actions;
export default authenticationSlice.reducer;
